import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import HolidayCalendar from './pages/HolidayCalendar';
import Dashboard from './pages/Dashboard';
import TimeTracking from './pages/TimeTracking';
import AttendanceHistory from './pages/AttendanceHistory';
import LeaveManagement from './pages/LeaveManagement';
import LeaveCredits from './pages/LeaveCredits';
import TeamManagement from './pages/TeamManagement';
import Reports from './pages/Reports';
import ManualTimeEntry from './pages/ManualTimeEntry';
import { Toaster } from './components/ui/sonner';
import './App.css';
import { authFetch, API } from './utils/api';

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        const response = await authFetch(`${API}/auth/me`);
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children({ user });
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            {({ user }) => <Profile user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/holidays"
        element={
          <ProtectedRoute>
            {({ user }) => <HolidayCalendar user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-tracking"
        element={
          <ProtectedRoute>
            {({ user }) => <TimeTracking user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/attendance"
        element={
          <ProtectedRoute>
            {({ user }) => <AttendanceHistory user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/leave"
        element={
          <ProtectedRoute>
            {({ user }) => <LeaveManagement user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/leave-credits"
        element={
          <ProtectedRoute>
            {({ user }) => <LeaveCredits user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            {({ user }) => <TeamManagement user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            {({ user }) => <Reports user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manual-time-entry"
        element={
          <ProtectedRoute>
            {({ user }) => <ManualTimeEntry user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;